/**
 * OUTLIER HUNTING — every building as one dot.
 *
 * ── THE QUESTION A RANKED LIST CANNOT ANSWER ──
 *
 * The league table answers "which building recorded the most". It cannot answer
 * "which building records an unusual NUMBER OF INCIDENTS FOR ITS SIZE", and that
 * is the question a portfolio manager actually has. A busy building with many
 * incidents is unremarkable; a quiet one with the same count is the site to
 * visit this week, and on a ranked bar chart it sits somewhere in the middle
 * where nobody looks.
 *
 * x = every record logged, y = the ones carrying an incident, one dot per
 * building. The diagonal is the portfolio's own overall rate — NOT a target, and
 * the caption says so. A dot above the line has more incidents per record than
 * the portfolio average; a dot below has fewer. That is a comparison against
 * this organisation's own experience rather than against a number somebody
 * invented.
 *
 * ── ZAxis CARRIES THE THIRD FACT ──
 *
 * Dot AREA is the record count, so a dot far above the line but tiny is visibly
 * a small sample — three incidents out of eleven records is a ratio nobody
 * should act on, and on a plain scatter it looks identical to thirty out of a
 * hundred and ten.
 *
 * ── AND IT IS NOT SHOWN FOR ONE BUILDING ──
 *
 * A scatter of one dot is a dot. Below three buildings the ranked list says more,
 * and the panel is not rendered at all.
 */
import React, { useMemo } from 'react';
import {
  CartesianGrid,
  Cell,
  ReferenceLine,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import { AnalyticsBatchResult, isBatchFailure } from '../../../../../api/analytics-client';
import { dataViz, palette } from '../../../../../style-utils/theme';
import { ChartShell, ChartShellState } from '../charts/chart-shell';
import { ChartTable } from '../charts/chart-table-view';
import { TipCard, TipLabel, TipNote, TipRow, TipStrong } from '../charts/chart.styled';
import {
  axisCommon,
  cursor,
  firstPaintAnimation,
  format,
  grid,
  margins,
  referenceLine,
} from '../charts/chart-theme';
import { readSlot, slotState } from './panel-utils';
import { DrillHandlers } from '../charts/use-chart-drill';

const PANEL_ID = 'outliers';

interface Dot {
  key: string;
  label: string;
  records: number;
  incidents: number;
  rate: number | null;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
const OutlierTip: React.FC<any> = ({ active, payload, overallRate }) => {
  if (!active || !payload?.length) return null;
  const dot = payload[0].payload as Dot;
  const above = dot.rate != null && overallRate != null && dot.rate > overallRate;

  return (
    <TipCard role="tooltip">
      <TipLabel>{dot.label}</TipLabel>
      <TipRow>
        <TipStrong>{dot.records.toLocaleString()}</TipStrong>
        <span>records</span>
      </TipRow>
      <TipRow>
        <TipStrong>{dot.incidents.toLocaleString()}</TipStrong>
        <span>with an incident</span>
        {dot.rate == null ? null : <span>· {dot.rate}%</span>}
      </TipRow>
      {dot.rate == null ? (
        <TipNote>No records here, so there is no share to state.</TipNote>
      ) : dot.records < 20 ? (
        <TipNote>
          Only {dot.records.toLocaleString()} records — too few for the share to mean
          much either way.
        </TipNote>
      ) : (
        <TipNote>
          {above ? 'Above' : 'At or below'} this organisation&apos;s own overall rate of{' '}
          {overallRate}%. That is a comparison with your own experience, not a target.
        </TipNote>
      )}
    </TipCard>
  );
};
/* eslint-enable @typescript-eslint/no-explicit-any */

export interface ScatterPanelProps {
  state: ChartShellState;
  /** Records per building. */
  league?: AnalyticsBatchResult;
  /** Incidents per building. */
  incidents?: AnalyticsBatchResult;
  drill: DrillHandlers;
  onExportPng?: (svg: SVGSVGElement | null) => void;
  onExportCsv?: () => void;
}

export const ScatterPanel: React.FC<ScatterPanelProps> = ({
  state,
  league,
  incidents,
  drill,
  onExportPng,
  onExportCsv,
}) => {
  const slot = readSlot(league);
  const data = slot.ok;

  const byKey = useMemo(() => {
    const map = new Map<string, number>();
    if (!incidents || isBatchFailure(incidents)) return map;
    for (const item of incidents.items) map.set(item.key, format.numberOf(item.value) ?? 0);

    return map;
  }, [incidents]);

  const dots = useMemo<Dot[]>(
    () =>
      (data?.items || []).map(item => {
        const records = format.numberOf(item.value) ?? 0;
        const bad = byKey.get(item.key) ?? 0;

        return {
          key: item.key,
          label: item.label,
          records,
          incidents: bad,
          rate: records > 0 ? Math.round((bad / records) * 1000) / 10 : null,
        };
      }),
    [data, byKey]
  );

  const totals = dots.reduce(
    (sum, dot) => ({
      records: sum.records + dot.records,
      incidents: sum.incidents + dot.incidents,
    }),
    { records: 0, incidents: 0 }
  );
  const overallRate =
    totals.records > 0 ? Math.round((totals.incidents / totals.records) * 1000) / 10 : null;

  const maxRecords = dots.reduce((max, dot) => Math.max(max, dot.records), 0);

  return (
    <ChartShell
      id={PANEL_ID}
      title="Incidents against volume, by building"
      wide
      aspect={2.2}
      minHeight={300}
      state={slotState(state, slot)}
      notes={slot.notes}
      caveat="One dot per building: how much it recorded, against how much of that carried an incident. The dot's SIZE is the record count, so a small dot far from the line is a small sample rather than a finding. The diagonal is this organisation's own overall rate — it is not a target, and being below it is not a pass."
      emptyReason={
        slot.empty
          ? {
              title: 'Nothing was recorded in this period',
              text: 'With no records there is nothing to place on either axis.',
            }
          : dots.length < 3
          ? {
              title: 'Too few buildings to hunt for outliers',
              text: 'An outlier is a building unlike the others, which needs others. Below three, the ranked list above says more than a scatter would.',
            }
          : null
      }
      summary={
        dots.length
          ? `Incidents against volume for ${dots.length} buildings; the organisation's overall rate is ${overallRate ?? 0}%.`
          : undefined
      }
      onExportPng={onExportPng}
      onExportCsv={onExportCsv}
      table={
        data ? (
          <ChartTable
            dimensionLabel="Building"
            valueLabel="Records"
            showCompare
            compareLabel="With an incident"
            deltaLabel="Share"
            rows={dots.map(dot => ({
              key: dot.key,
              label: dot.label,
              value: dot.records.toLocaleString(),
              compareValue: dot.incidents.toLocaleString(),
              delta: dot.rate == null ? 'no records' : `${dot.rate}%`,
            }))}
          />
        ) : undefined
      }
    >
      <ScatterChart accessibilityLayer margin={margins.default}>
        <CartesianGrid {...grid} vertical />
        <XAxis
          type="number"
          dataKey="records"
          name="Records"
          {...axisCommon}
          allowDecimals={false}
        />
        <YAxis
          type="number"
          dataKey="incidents"
          name="Incidents"
          {...axisCommon}
          width={44}
          allowDecimals={false}
        />
        {/* AREA, not radius: doubling the radius quadruples the ink, and a
            reader reads the ink. Recharts scales the range as area. */}
        <ZAxis type="number" dataKey="records" range={[60, 900]} name="Records" />

        <Tooltip
          content={<OutlierTip overallRate={overallRate} />}
          cursor={cursor.line}
        />

        {overallRate != null && maxRecords > 0 ? (
          <ReferenceLine
            segment={[
              { x: 0, y: 0 },
              { x: maxRecords, y: (maxRecords * overallRate) / 100 },
            ]}
            {...referenceLine}
            ifOverflow="extendDomain"
          />
        ) : null}

        <Scatter
          data={dots}
          fill={dataViz.series[0]}
          onClick={drill.onMark}
          cursor="pointer"
          {...firstPaintAnimation(PANEL_ID)}
        >
          {dots.map(dot => (
            <Cell
              key={dot.key}
              fill={
                dot.records < 20
                  ? palette.slateLight
                  : dot.rate != null && overallRate != null && dot.rate > overallRate
                  ? palette.warning
                  : dataViz.series[0]
              }
              fillOpacity={0.75}
            />
          ))}
        </Scatter>
      </ScatterChart>
    </ChartShell>
  );
};
